import { logger } from '../utils/logger';

/**
 * 股票資料介面
 */
export interface StockData {
  symbol: string;      // 股票代號 (必需，唯一標識)
  name?: string;       // 股票名稱 (可選)
  market?: string;     // 市場代碼 (TSE, OTC 等)
  updatedAt?: Date;    // 最後更新時間
}

/**
 * 統一的股票名稱管理器
 * 
 * 設計原則：
 * 1. 所有股票資料集中管理
 * 2. 只有確認有效的資料才會更新
 * 3. 提供統一的查詢接口
 * 4. 消除各地方的判斷邏輯
 */
class StockNameManagerClass {
  private stockDataMap = new Map<string, StockData>();
  
  /**
   * 驗證股票名稱是否有效
   * @param name 股票名稱
   * @param symbol 股票代號
   * @returns 是否為有效名稱
   */
  private isValidName(name: string, symbol: string): boolean {
    if (!name || typeof name !== 'string') {
      logger.stockNameManager.debug(`🚫 名稱驗證失敗: 不是有效字串`, { name, symbol, type: typeof name });
      return false;
    }
    
    const cleanName = name.trim();
    const cleanSymbol = symbol.trim().toUpperCase();
    
    // 空字串
    if (cleanName === '') {
      logger.stockNameManager.debug(`🚫 名稱驗證失敗: 空字串`, { name, symbol });
      return false;
    }
    
    // 等於代號本身
    if (cleanName === cleanSymbol) {
      logger.stockNameManager.debug(`🚫 名稱驗證失敗: 等於股票代號`, { name: cleanName, symbol: cleanSymbol });
      return false;
    }
    
    // 包含錯誤訊息 - 加強檢查
    const errorKeywords = ['查無資料', '無資料', '未知', '無法取得', '錯誤', '失敗', 'undefined', 'null', 'N/A', 'n/a'];
    for (const keyword of errorKeywords) {
      if (cleanName.includes(keyword)) {
        logger.stockNameManager.warn(`🚫 名稱驗證失敗: 包含錯誤關鍵字 "${keyword}"`, { name: cleanName, symbol: cleanSymbol });
        return false;
      }
    }
    
    // 異常長度
    if (cleanName.length > 50) {
      logger.stockNameManager.debug(`🚫 名稱驗證失敗: 長度過長 (${cleanName.length} > 50)`, { name: cleanName, symbol: cleanSymbol });
      return false;
    }
    
    // 代號重複問題 (如 "28882888")
    if (cleanName.includes(cleanSymbol + cleanSymbol)) {
      logger.stockNameManager.warn(`🚫 名稱驗證失敗: 包含重複代號`, { name: cleanName, symbol: cleanSymbol, repeatedPattern: cleanSymbol + cleanSymbol });
      return false;
    }
    
    // 檢查是否只包含數字（可能是錯誤的代號）
    if (/^\d+$/.test(cleanName) && cleanName !== cleanSymbol) {
      logger.stockNameManager.debug(`🚫 名稱驗證失敗: 純數字名稱疑似錯誤`, { name: cleanName, symbol: cleanSymbol });
      return false;
    }
    
    // 移除重複日誌以減少噪音
    return true;
  }
  
  /**
   * 驗證市場代碼是否有效
   * @param market 市場代碼
   * @returns 是否為有效市場代碼
   */
  private isValidMarket(market: string): boolean {
    if (!market || typeof market !== 'string') return false;
    const cleanMarket = market.trim().toUpperCase();
    return cleanMarket.length > 0 && cleanMarket.length <= 10;
  }
  
  /**
   * 更新股票資料
   * 只有確認有效的資料才會更新
   * 
   * @param symbol 股票代號 (必需)
   * @param name 股票名稱 (可選，只有有效時才更新)
   * @param market 市場代碼 (可選，只有有效時才更新)
   */
  updateStock(symbol: string, name?: string, market?: string): void {
    if (!symbol || typeof symbol !== 'string') {
      logger.stockNameManager.warn('updateStock: 無效的股票代號', { symbol });
      return;
    }
    
    const cleanSymbol = symbol.trim().toUpperCase();
    if (cleanSymbol === '') {
      logger.stockNameManager.warn('updateStock: 空的股票代號', { symbol });
      return;
    }
    
    // 獲取現有資料或創建新的
    const existingData = this.stockDataMap.get(cleanSymbol) || { symbol: cleanSymbol };
    const updatedData: StockData = { ...existingData };
    let hasUpdate = false;
    
    // 加強名稱更新保護機制
    if (name !== undefined) {
      logger.stockNameManager.debug(`🔍 嘗試更新 ${cleanSymbol} 名稱`, {
        symbol: cleanSymbol,
        inputName: name,
        existingName: existingData.name,
        hasExistingName: !!existingData.name
      });
      
      // 檢查新名稱是否有效
      if (this.isValidName(name, cleanSymbol)) {
        const cleanName = name.trim();
        
        // 如果已經有有效的現有名稱，需要額外保護
        if (existingData.name && this.isValidName(existingData.name, cleanSymbol)) {
          // 只有在新名稱更好或不同時才更新
          if (cleanName !== existingData.name) {
            logger.stockNameManager.info(`🔄 ${cleanSymbol} 有效名稱更新`, {
              symbol: cleanSymbol,
              oldName: existingData.name,
              newName: cleanName,
              reason: '新的有效名稱'
            });
            updatedData.name = cleanName;
            hasUpdate = true;
          } else {
            logger.stockNameManager.debug(`✅ ${cleanSymbol} 名稱無變化，保持不變`, {
              symbol: cleanSymbol,
              name: cleanName
            });
          }
        } else {
          // 沒有現有名稱或現有名稱無效，直接更新
          logger.stockNameManager.info(`📝 ${cleanSymbol} 新增有效名稱`, {
            symbol: cleanSymbol,
            oldName: existingData.name || '無',
            newName: cleanName
          });
          updatedData.name = cleanName;
          hasUpdate = true;
        }
      } else {
        // 新名稱無效，記錄警告但保持現有名稱不變
        logger.stockNameManager.warn(`⚠️ ${cleanSymbol} 拒絕無效名稱更新`, {
          symbol: cleanSymbol,
          invalidName: name,
          existingName: existingData.name || '無',
          action: '保持現有名稱不變'
        });
      }
    }
    
    // 只有市場代碼有效時才更新
    if (market && this.isValidMarket(market)) {
      const cleanMarket = market.trim().toUpperCase();
      if (cleanMarket !== existingData.market) {
        updatedData.market = cleanMarket;
        hasUpdate = true;
        logger.stockNameManager.debug('更新市場代碼', { symbol: cleanSymbol, oldMarket: existingData.market, newMarket: cleanMarket });
      }
    }
    
    // 只有有更新時才保存並記錄時間
    if (hasUpdate || !this.stockDataMap.has(cleanSymbol)) {
      updatedData.updatedAt = new Date();
      this.stockDataMap.set(cleanSymbol, updatedData);
      
      if (hasUpdate) {
        logger.stockNameManager.info('✅ 股票資料已更新', {
          symbol: cleanSymbol, 
          name: updatedData.name, 
          market: updatedData.market,
          timestamp: updatedData.updatedAt
        });
      }
    }
  }
  
  /**
   * 智能更新股票名稱 - 支援多個資料來源的優先選擇
   * 這是核心的智能更新方法，統一處理各種即時資料來源
   * 
   * @param symbol 股票代號 (必需)
   * @param sources 多個資料來源的名稱候選清單
   */
  smartUpdateStock(symbol: string, sources: {
    apiName?: string;           // API 回傳的名稱
    realtimeName?: string;      // 即時資料中的名稱
    staticName?: string;        // 靜態資料中的名稱
    transactionName?: string;   // 交易紀錄中儲存的名稱
    userDefinedName?: string;   // 用戶自訂名稱
    market?: string;            // 市場代碼
  }): boolean {
    if (!symbol || typeof symbol !== 'string') {
      logger.stockNameManager.warn('smartUpdateStock: 無效的股票代號', { symbol });
      return false;
    }
    
    const cleanSymbol = symbol.trim().toUpperCase();
    if (cleanSymbol === '') {
      logger.stockNameManager.warn('smartUpdateStock: 空的股票代號', { symbol });
      return false;
    }
    
    // 取得現有資料
    const existingData = this.stockDataMap.get(cleanSymbol);
    const currentName = existingData?.name;
    
    // 預設已知股票名稱 (可以擴展)
    const knownStocks: Record<string, string> = {
      '2330': '台積電',
      '2317': '鴻海',
      '2454': '聯發科',
      '2888': '新光金',
      '0050': '元大台灣50',
      // 可根據需要添加更多常見股票
    };
    
    // 名稱候選清單，按優先順序排列
    const candidates = [
      { source: '用戶自訂', value: sources.userDefinedName },
      { source: 'API', value: sources.apiName },
      { source: '即時資料', value: sources.realtimeName },
      { source: '靜態資料', value: sources.staticName },
      { source: '交易紀錄', value: sources.transactionName },
      { source: '已知股票', value: knownStocks[cleanSymbol] },
      { source: '現有名稱', value: currentName },
    ];
    
    logger.stockNameManager.debug(`🔍 智能更新股票名稱 ${cleanSymbol}`, {
      symbol: cleanSymbol,
      currentName,
      sources,
      candidateCount: candidates.filter(c => c.value).length
    });
    
    // 找出最佳名稱
    let bestName: string | null = null;
    let bestSource = '';
    
    for (const candidate of candidates) {
      if (candidate.value && this.isValidName(candidate.value, cleanSymbol)) {
        const cleanCandidateName = candidate.value.trim();
        
        // 額外檢查：防止名稱重複連接或過長
        if (cleanCandidateName.includes(cleanSymbol + cleanSymbol) || cleanCandidateName.length > 50) {
          logger.stockNameManager.warn(`🚫 ${cleanSymbol} ${candidate.source}名稱疑似有問題，跳過:`, {
            value: cleanCandidateName,
            length: cleanCandidateName.length,
            hasDoubleSymbol: cleanCandidateName.includes(cleanSymbol + cleanSymbol)
          });
          continue;
        }
        
        bestName = cleanCandidateName;
        bestSource = candidate.source;
        break;
      }
    }
    
    if (!bestName) {
      logger.stockNameManager.warn(`⚠️ ${cleanSymbol} 找不到有效名稱，不進行更新`, {
        symbol: cleanSymbol,
        candidatesChecked: candidates.length
      });
      return false;
    }
    
    // 檢查是否需要更新
    if (bestName === currentName) {
      logger.stockNameManager.debug(`✅ ${cleanSymbol} 名稱無變化，無需更新: "${bestName}"`);
      return false;
    }
    
    // 執行更新
    this.updateStock(cleanSymbol, bestName, sources.market);
    
    logger.stockNameManager.info(`🎯 ${cleanSymbol} 智能更新完成`, {
      symbol: cleanSymbol,
      source: bestSource,
      oldName: currentName || '無',
      newName: bestName
    });
    
    return true;
  }
  
  /**
   * 批量智能更新股票名稱
   * @param updates 批量更新的資料
   */
  smartUpdateBatch(updates: Array<{
    symbol: string;
    sources: {
      apiName?: string;
      realtimeName?: string;
      staticName?: string;
      transactionName?: string;
      userDefinedName?: string;
      market?: string;
    };
  }>): { updated: number; skipped: number; errors: number } {
    const result = { updated: 0, skipped: 0, errors: 0 };
    
    logger.stockNameManager.info('🚀 開始批量智能更新股票名稱', {
      totalCount: updates.length
    });
    
    updates.forEach(update => {
      try {
        const wasUpdated = this.smartUpdateStock(update.symbol, update.sources);
        if (wasUpdated) {
          result.updated++;
        } else {
          result.skipped++;
        }
      } catch (error) {
        logger.stockNameManager.error('批量更新時發生錯誤', {
          symbol: update.symbol,
          error
        });
        result.errors++;
      }
    });
    
    logger.stockNameManager.info('✅ 批量智能更新完成', result);
    return result;
  }
  
  /**
   * 批量更新股票資料
   * @param stocks 股票資料陣列
   */
  updateStocks(stocks: Array<{ symbol: string; name?: string; market?: string }>): void {
    stocks.forEach(stock => {
      this.updateStock(stock.symbol, stock.name, stock.market);
    });
  }
  
  /**
   * 獲取股票名稱
   * 統一的查詢接口，消除各地方的判斷邏輯
   * 
   * @param symbol 股票代號
   * @returns 股票名稱 (如果找不到有效名稱，返回帶有查無資料的格式)
   */
  getStockName(symbol: string): string {
    if (!symbol || typeof symbol !== 'string') {
      return '未知股票';
    }
    
    const cleanSymbol = symbol.trim().toUpperCase();
    if (cleanSymbol === '') {
      return '未知股票';
    }
    
    // 特殊處理現金
    if (cleanSymbol === 'CASH') {
      return '現金調整';
    }
    
    const stockData = this.stockDataMap.get(cleanSymbol);
    
    // 如果有有效名稱，直接返回
    if (stockData?.name && this.isValidName(stockData.name, cleanSymbol)) {
      return stockData.name;
    }
    
    // 找不到有效名稱，返回查無資料格式
    return `${cleanSymbol} (查無資料)`;
  }
  
  /**
   * 獲取股票完整資料
   * @param symbol 股票代號
   * @returns 股票資料 (如果不存在返回 null)
   */
  getStockData(symbol: string): StockData | null {
    if (!symbol || typeof symbol !== 'string') return null;
    
    const cleanSymbol = symbol.trim().toUpperCase();
    return this.stockDataMap.get(cleanSymbol) || null;
  }
  
  /**
   * 檢查股票是否有有效名稱
   * @param symbol 股票代號
   * @returns 是否有有效名稱
   */
  hasValidName(symbol: string): boolean {
    if (!symbol || typeof symbol !== 'string') return false;
    
    const cleanSymbol = symbol.trim().toUpperCase();
    const stockData = this.stockDataMap.get(cleanSymbol);
    
    return stockData?.name ? this.isValidName(stockData.name, cleanSymbol) : false;
  }
  
  /**
   * 獲取所有股票資料
   * @returns 所有股票資料的陣列
   */
  getAllStocks(): StockData[] {
    return Array.from(this.stockDataMap.values());
  }
  
  /**
   * 獲取指定市場的股票
   * @param market 市場代碼
   * @returns 該市場的股票資料陣列
   */
  getStocksByMarket(market: string): StockData[] {
    if (!market || typeof market !== 'string') return [];
    
    const cleanMarket = market.trim().toUpperCase();
    return Array.from(this.stockDataMap.values()).filter(stock => 
      stock.market === cleanMarket
    );
  }
  
  /**
   * 清空所有資料 (主要用於測試)
   */
  clear(): void {
    this.stockDataMap.clear();
    logger.stockNameManager.info('股票名稱管理器已清空');
  }
  
  /**
   * 獲取統計資訊
   */
  getStats(): {
    totalStocks: number;
    stocksWithValidName: number;
    stocksWithMarket: number;
  } {
    const stocks = this.getAllStocks();
    return {
      totalStocks: stocks.length,
      stocksWithValidName: stocks.filter(stock => 
        stock.name && this.isValidName(stock.name, stock.symbol)
      ).length,
      stocksWithMarket: stocks.filter(stock => stock.market).length
    };
  }
}

// 創建並導出單例實例
export const StockNameManager = new StockNameManagerClass();

// 同時導出類別供測試使用
export { StockNameManagerClass };
